/**
 * cart-pricing — shipping cost + total calculator for the cart checkout.
 *
 * TASK-FEAT-CART-UNIFIED-CHECKOUT (2026-11-05): the CartCheckout modal
 * needs to compute the shipping cost + free-shipping threshold for a
 * group of items from the SAME seller (so the buyer sees the same
 * information as the "Comprar ahora" modal on the product detail page).
 *
 * Why a separate module (not reusing `checkout-modal/helpers.ts:estimateOrder`):
 *   - `estimateOrder` operates on a SINGLE product + variation + quantity.
 *     The cart operates on a GROUP of items (potentially different products
 *     from the same seller). We need to:
 *       1. Sum the subtotal across all items.
 *       2. Resolve shipping ONCE per seller (the seller's shipping config
 *          is shared across all their items — the BE charges shipping per
 *          ORDER, not per product, and there's 1 order per item but the
 *          threshold is evaluated against the seller's cart subtotal).
 *       3. Apply free-shipping threshold against the seller's cart subtotal.
 *
 * Backend semantics (mirrored here for UX display):
 *   - The BE creates 1 order per item (INV-MKT-12 — separate escrow per item).
 *   - Each order's `cartTotalCredits` is the seller's cart subtotal (passed
 *     by the FE) — used for `validateShippingMinimum` (minOrderCredits).
 *   - The BE's `computeShippingCost` evaluates free-shipping threshold
 *     against the SINGLE order's totalPrice (NOT the cart subtotal).
 *     ⚠️ This means the BE may charge shipping per item even when the
 *     cart subtotal exceeds the threshold. The FE display here shows the
 *     "ideal" cart-level threshold (what the buyer EXPECTS) — the actual
 *     per-order shipping may differ. We display a clarifying note when
 *     this is the case.
 *
 * Anti-P-041: 0 `as any`.
 * Anti-P-042: 0 `console.*`.
 * Anti-P-382: REUSES the `resolveShipping`-style logic from the product
 *             detail page (single source of truth for shipping resolution),
 *             adapted for the CartItem shape.
 *
 * Spec ref: SPEC-081 Fase 4 §3.1 (INV-MKT-21 — fulfillment inheritance);
 *           Founder directive 2026-10-05 (per-product FULL fulfillment).
 *           Task ID: TASK-FEAT-CART-UNIFIED-CHECKOUT.
 */

import type { CartItem } from './store';

export interface CartShippingQuote {
  /** Whether the seller offers shipping at all. */
  ships: boolean;
  /** Whether the seller offers pickup. */
  pickup: boolean;
  /** The shipping cost (in créditos, parsed to number). 0 if free shipping applies. */
  shippingCost: number;
  /** The free-shipping threshold (in créditos), or null if none set. */
  freeShippingThreshold: number | null;
  /** The minimum order credits (in créditos) for shipping to be offered. */
  minOrderCredits: number;
  /** True if the cart subtotal meets or exceeds the free-shipping threshold. */
  freeShippingApplied: boolean;
  /** The cart subtotal (Σ priceCredits × qty). */
  subtotal: number;
  /** The grand total (subtotal + shippingCost). */
  grandTotal: number;
  /** The pickup address (if pickup is offered). */
  pickupAddress: string | null;
  /** The pickup hours (if pickup is offered). */
  pickupHours: string | null;
  /** The pickup instructions (if pickup is offered). */
  pickupInstructions: string | null;
}

/**
 * Compute the shipping quote for a group of cart items from the same seller.
 *
 * Resolution strategy (mirrors `checkout-modal/helpers.ts:resolveShipping`):
 *   - If ANY item has `fulfillment.inheritSeller=true` → use the seller's
 *     shipping config from `sellerFulfillmentOptions.shipping`.
 *   - If ANY item has `fulfillment.inheritSeller=false` + `config.shipping`
 *     → use the product's own shipping config (but pick the FIRST one —
 *     items from the same seller should have the same shipping config;
 *     if they differ, the BE rejects with FULFILLMENT_NOT_SUPPORTED on
 *     the mismatching item).
 *   - If ANY item has `shippingOverride` (legacy) → use it.
 *   - If NONE of the items have fulfillment data (old cart items pre-fix),
 *     return `null` for shipping display (graceful degradation).
 *
 * The threshold check uses the cart subtotal (Σ items × price), NOT the
 * per-item price. This matches the buyer's mental model: "if I buy enough
 * from this seller, I get free shipping."
 *
 * @param sellerItems Items from the same seller (caller groups them).
 * @returns The shipping quote, or null if fulfillment data is missing.
 */
export function computeCartShipping(sellerItems: CartItem[]): CartShippingQuote | null {
  if (sellerItems.length === 0) return null;

  // Pick the first item that has fulfillment data. Items from the same
  // seller should have the same shipping config (the BE enforces this on
  // product creation). If they differ, we still use the first one — the
  // BE will reject the mismatching item with a clear error.
  const itemWithFulfillment = sellerItems.find((i) => i.fulfillment && i.sellerFulfillmentOptions);
  if (!itemWithFulfillment || !itemWithFulfillment.fulfillment || !itemWithFulfillment.sellerFulfillmentOptions) {
    // Old cart item (pre-fix) — no fulfillment data captured. Return null
    // so CartCheckout can render a "reload the product page" callout.
    return null;
  }

  const f = itemWithFulfillment.fulfillment;
  const sellerOpts = itemWithFulfillment.sellerFulfillmentOptions;

  // Compute subtotal across all items.
  const subtotal = sellerItems.reduce(
    (sum, i) => sum + parseFloat(i.priceCredits) * i.quantity,
    0,
  );

  // Resolve shipping config (mirrors resolveShipping from checkout-modal/helpers.ts).
  let shippingCostBase = 0;
  let freeShippingThreshold: number | null = null;
  let minOrderCredits = 0;
  let ships = false;

  const offersShips = (f.modalities ?? []).includes('ships');

  if (f.inheritSeller) {
    if (!sellerOpts.ships) {
      // Seller doesn't ship — only pickup available.
      ships = false;
    } else {
      ships = true;
      const sellerShipping = sellerOpts.shipping;
      if (sellerShipping) {
        shippingCostBase = parseFloat(sellerShipping.cost) || 0;
        freeShippingThreshold = sellerShipping.freeShippingThreshold
          ? parseFloat(sellerShipping.freeShippingThreshold)
          : null;
        minOrderCredits = parseFloat(sellerShipping.minOrderCredits) || 0;
      }
    }
  } else if (f.config?.shipping) {
    // Founder directive 2026-10-05 — per-product FULL shipping config.
    ships = true;
    shippingCostBase = parseFloat(f.config.shipping.cost) || 0;
    freeShippingThreshold = f.config.shipping.freeShippingThreshold
      ? parseFloat(f.config.shipping.freeShippingThreshold)
      : null;
    minOrderCredits = parseFloat(f.config.shipping.minOrderCredits ?? '0') || 0;
  } else {
    // Legacy override.
    const override = f.shippingOverride;
    if (override && (offersShips || (f.modalities ?? []).length === 0)) {
      if (sellerOpts.ships && sellerOpts.shipping) {
        ships = true;
        shippingCostBase = parseFloat(override.cost) || 0;
        freeShippingThreshold = override.freeShippingThreshold
          ? parseFloat(override.freeShippingThreshold)
          : null;
        minOrderCredits = parseFloat(override.minOrderCredits) || 0;
      }
    } else if (offersShips) {
      ships = true;
      if (sellerOpts.shipping) {
        shippingCostBase = parseFloat(sellerOpts.shipping.cost) || 0;
        freeShippingThreshold = sellerOpts.shipping.freeShippingThreshold
          ? parseFloat(sellerOpts.shipping.freeShippingThreshold)
          : null;
        minOrderCredits = parseFloat(sellerOpts.shipping.minOrderCredits) || 0;
      }
    }
  }

  // Resolve pickup info.
  let pickupAddress: string | null = null;
  let pickupHours: string | null = null;
  let pickupInstructions: string | null = null;
  const offersPickup = (f.modalities ?? []).includes('pickup');
  if (f.inheritSeller) {
    if (sellerOpts.pickup && sellerOpts.pickupConfig) {
      pickupAddress = sellerOpts.pickupConfig.location.address;
      pickupHours = sellerOpts.pickupConfig.hours;
      pickupInstructions = sellerOpts.pickupConfig.instructions;
    }
  } else if (offersPickup) {
    const customPickup = f.config?.pickup;
    const customLoc = customPickup?.locations?.[0];
    if (customLoc) {
      pickupAddress = customLoc.address;
      pickupHours = customPickup?.hours ?? '';
      pickupInstructions = customPickup?.instructions ?? '';
    } else if (sellerOpts.pickup && sellerOpts.pickupConfig) {
      pickupAddress = sellerOpts.pickupConfig.location.address;
      pickupHours = sellerOpts.pickupConfig.hours;
      pickupInstructions = sellerOpts.pickupConfig.instructions;
    }
  }

  // Free-shipping threshold check (against cart subtotal — buyer's mental model).
  const freeShippingApplied =
    freeShippingThreshold !== null &&
    !Number.isNaN(freeShippingThreshold) &&
    subtotal >= freeShippingThreshold;
  const shippingCost = freeShippingApplied ? 0 : shippingCostBase;
  const grandTotal = subtotal + shippingCost;

  return {
    ships,
    pickup: pickupAddress !== null,
    shippingCost,
    freeShippingThreshold,
    minOrderCredits,
    freeShippingApplied,
    subtotal,
    grandTotal,
    pickupAddress,
    pickupHours,
    pickupInstructions,
  };
}
